import { child, get, push, ref, remove, set, update } from 'firebase/database'

import { db } from './firebase.js'
import { createDemoTournament } from './demos.js'
import { ensureTournamentInfo } from './data.js'
import CLUB from '../config/club.js'

// Fixed pseudo-tournament that owns the /testphoto + /testphotowall data so
// the wall has a stable read path that doesn't shift per session.
export const TEST_PHOTO_TOURNAMENT_ID = '__testphotos__'

// Persistent scorecard fixture. Unlike seedScorecardTest (a throwaway
// tournament per run), this is ONE fixed tournament + team so the Test
// Station QR never changes — Mark scans once and always lands on the live
// scorecard UI to eyeball changes. It is deliberately excluded from the
// "Clean Up All Test Data" sweep and the active-test counter, and is
// self-healed by ensureScorecardTestTournament() if it ever gets removed.
export const SCORECARD_TEST_TOURNAMENT_ID = '-OvwJKOs7oUOWhYG3zM1'
export const SCORECARD_TEST_FLIGHT_ID = 'flightA'
export const SCORECARD_TEST_TEAM_ID = 'team1'

// Test Station tournaments reuse the demo seed factories but get an extra
// info.isTestStation flag so the admin dashboard can filter them out of
// Demos / My Tournaments / Closed lists. The test station page is the
// only surface that lists them.
export async function seedTestTournament(formatKey, opts = {}) {
  const { tournamentId, def } = await createDemoTournament(formatKey)
  const patch = {
    isTestStation: true,
    testStationTile: opts.tile || 'misc',
    testStationCreatedAt: Date.now(),
  }
  if (opts.name) patch.name = opts.name
  await update(ref(db, `tournaments/${tournamentId}/info`), patch)
  return { tournamentId, def }
}

export async function deleteTestTournament(tournamentId) {
  if (!tournamentId) return
  await remove(ref(db, `tournaments/${tournamentId}`))
}

// Sweep every tournament tagged isTestStation. Used by the "Clean Up All
// Test Data" button at the top of /teststation. Also nukes the fixed
// __testphotos__ pseudo-tournament so the wall starts clean next session.
export async function deleteAllTestTournaments() {
  const snap = await get(child(ref(db), 'tournaments'))
  const all = snap.val() || {}
  let removed = 0
  for (const [id, t] of Object.entries(all)) {
    // Never sweep the persistent scorecard fixture — its whole point is to
    // survive cleanups so the Test Station QR keeps working.
    if (id === SCORECARD_TEST_TOURNAMENT_ID) continue
    if (!t?.info?.isTestStation) continue
    await remove(ref(db, `tournaments/${id}`))
    removed++
  }
  // Also clear the test photo store regardless of whether tiles exist.
  try {
    await remove(ref(db, `tournaments/${TEST_PHOTO_TOURNAMENT_ID}`))
  } catch {}
  return removed
}

// Custom scorecard test seeder — gives an exact team count instead of the
// 18-team Scramble demo so the leaderboard demo on /testleaderboard stays
// focused. Empty score maps so testers fill them in themselves.
const TEST_PARS = (CLUB.course?.pars && CLUB.course.pars.length === 18)
  ? CLUB.course.pars
  : [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 4, 3, 5, 4, 4, 3, 4, 5]

const TEST_TEAM_NAMES = [
  { name: 'Ratliff/Keese', players: ['Ratliff', 'Keese'] },
  { name: 'Bryant',  players: ['Cash Bryant', 'Beau Bryant'] },
  { name: 'Spencer', players: ['Wyatt Spencer', 'Cade Spencer'] },
  { name: 'Mason',   players: ['Levi Mason', 'Brett Mason'] },
]

export async function seedScorecardTest(teamCount = 4) {
  const count = Math.max(1, Math.min(4, teamCount | 0))
  const tournamentId = push(ref(db, 'tournaments')).key
  const teams = {}
  for (let i = 0; i < count; i++) {
    const row = TEST_TEAM_NAMES[i]
    const teamId = `team${i + 1}`
    teams[teamId] = {
      info: {
        name: row.name,
        players: row.players,
        startingHole: i + 1,
      },
    }
  }
  const tree = {
    info: {
      name: 'PBCC 4-Ball Championship',
      format: 'Scramble',
      playStyle: 'stroke',
      teeStyle: 'Shotgun start',
      status: 'active',
      course: CLUB.course?.name || 'Test Course',
      date: new Date().toISOString().slice(0, 10),
      pars: TEST_PARS,
      isDemo: true,
      isTestStation: true,
      testStationTile: 'scorecard',
      testStationCreatedAt: Date.now(),
      demoCreatedAt: Date.now(),
      createdAt: Date.now(),
    },
    flights: {
      flightA: {
        info: { name: 'Test Flight', format: 'Scramble' },
        teams,
      },
    },
  }
  ensureTournamentInfo(tree.info)
  await set(ref(db, `tournaments/${tournamentId}`), tree)
  return { tournamentId, teamCount: count, flightId: 'flightA', teams }
}

// Guarantee the persistent scorecard fixture exists so its fixed QR always
// opens a working scorecard. Idempotent: if the whole tournament is gone
// (e.g. someone deleted it directly), rebuild a known-good tree; if only the
// dummy team is missing, re-add just that team. Returns the stable
// tournament/flight/team ids the Test Station QR points at.
export async function ensureScorecardTestTournament() {
  const tid = SCORECARD_TEST_TOURNAMENT_ID
  const teamPath = `tournaments/${tid}/flights/${SCORECARD_TEST_FLIGHT_ID}/teams/${SCORECARD_TEST_TEAM_ID}`
  const [infoSnap, teamSnap] = await Promise.all([
    get(ref(db, `tournaments/${tid}/info`)),
    get(ref(db, teamPath)),
  ])

  // Permanent team for the persistent scorecard fixture. Keep this name in
  // sync with the live DB value — recreates/self-heals must NOT reset it back
  // to "Test Team" or a demo name like "Hayes".
  const dummyTeam = {
    info: { name: 'Ratliff/Keese', players: ['Ratliff', 'Keese'], startingHole: 1 },
  }

  if (!infoSnap.exists()) {
    const tree = {
      info: {
        name: 'TEST · Scorecard (persistent)',
        format: 'Scramble',
        playStyle: 'stroke',
        teeStyle: 'Shotgun start',
        status: 'active',
        course: CLUB.course?.name || 'Test Course',
        date: new Date().toISOString().slice(0, 10),
        pars: TEST_PARS,
        isDemo: true,
        isTestStation: true,
        testStationTile: 'scorecard-persistent',
        testStationCreatedAt: Date.now(),
        demoCreatedAt: Date.now(),
        createdAt: Date.now(),
      },
      flights: {
        [SCORECARD_TEST_FLIGHT_ID]: {
          info: { name: 'Test Flight', format: 'Scramble' },
          teams: { [SCORECARD_TEST_TEAM_ID]: dummyTeam },
        },
      },
    }
    ensureTournamentInfo(tree.info)
    await set(ref(db, `tournaments/${tid}`), tree)
  } else if (!teamSnap.exists()) {
    await set(ref(db, teamPath), dummyTeam)
  } else if (teamSnap.val()?.info?.name !== 'Ratliff/Keese') {
    // Team exists but under a stale name (e.g. "Hayes" / "Test Team") — force
    // it back to the permanent name on every run. Updates only info/name, so
    // any saved scores (under teamPath/scores) are left untouched.
    await update(ref(db, `${teamPath}/info`), { name: 'Ratliff/Keese' })
  }

  return {
    tournamentId: tid,
    flightId: SCORECARD_TEST_FLIGHT_ID,
    teamId: SCORECARD_TEST_TEAM_ID,
  }
}

// Wipe just the test photo store. Called by the Photo Wall tile's
// per-tile "Clean Up" button so other test data is left intact.
export async function deleteTestPhotos() {
  await remove(ref(db, `tournaments/${TEST_PHOTO_TOURNAMENT_ID}`))
}

// Pace of Play test seed. Supports 1 or 2 teams so a single tester can
// scan one QR and watch the timer fire without a partner; two teams gives
// independent groups (holes 1 + 2) for testing source-vs-delayed.
//
// Format is Scramble (single-team scoring) so each save reliably calls
// recordHoleSavedForGroup — that's the timer reset hook.
//
// COMPRESSED 5-MIN BUDGET: real tournaments use the 15-min PACE_LIMIT_MS
// default, but the test seed stamps info.paceLimitMs / paceWarnMs so the
// scorecard + Command Center use a 5-min limit with a 3-min warn point
// (yellow appears at 2:00 remaining, red fires at 0:00). Real tournaments
// leave these fields unset and inherit the 15/12-min production constants.
export async function seedPaceOfPlayTest(teamCount = 2) {
  const count = Math.max(1, Math.min(2, teamCount | 0))
  const tournamentId = push(ref(db, 'tournaments')).key
  const rows = [
    { id: 'team1', name: 'Group A (Hole 1)', players: ['Ratliff', 'Keese'],    startingHole: 1 },
    { id: 'team2', name: 'Group B (Hole 2)', players: ['Cash Bryant', 'Beau Bryant'], startingHole: 2 },
  ]
  const teams = {}
  for (let i = 0; i < count; i++) {
    const r = rows[i]
    teams[r.id] = { info: { name: r.name, players: r.players, startingHole: r.startingHole } }
  }
  const tree = {
    info: {
      name: 'TEST · Pace of Play',
      format: 'Scramble',
      playStyle: 'stroke',
      teeStyle: 'Shotgun start',
      status: 'active',
      course: CLUB.course?.name || 'Test Course',
      date: new Date().toISOString().slice(0, 10),
      pars: TEST_PARS,
      isDemo: true,
      isTestStation: true,
      testStationTile: 'pace',
      testStationCreatedAt: Date.now(),
      demoCreatedAt: Date.now(),
      createdAt: Date.now(),
      // 5-min red / 3-min warn (2 min remaining = yellow). Test-only.
      paceLimitMs: 5 * 60 * 1000,
      paceWarnMs:  3 * 60 * 1000,
    },
    flights: {
      flightA: {
        info: { name: 'Pace Test Flight', format: 'Scramble' },
        teams,
      },
    },
  }
  ensureTournamentInfo(tree.info)
  await set(ref(db, `tournaments/${tournamentId}`), tree)
  return { tournamentId, flightId: 'flightA', teams, teamCount: count }
}
